
import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { Wifi, Car, AirVent, Shield, Waves, Dumbbell, TreePine, Camera, Utensils, WashingMachine } from 'lucide-react';

interface PropertyAmenitiesProps {
  selectedAmenities: string[];
  onAmenitiesChange: (amenities: string[]) => void;
}

const PropertyAmenities = ({ selectedAmenities, onAmenitiesChange }: PropertyAmenitiesProps) => {
  const { t } = useLanguage();

  const amenities = [
    {
      id: 'wifi',
      icon: Wifi,
      label: {
        en: 'WiFi',
        ar: 'واي فاي'
      }
    },
    {
      id: 'parking',
      icon: Car,
      label: {
        en: 'Parking',
        ar: 'موقف سيارات'
      }
    },
    {
      id: 'ac',
      icon: AirVent,
      label: {
        en: 'Air Conditioning',
        ar: 'تكييف'
      }
    },
    {
      id: 'security',
      icon: Shield,
      label: {
        en: 'Security',
        ar: 'أمن'
      }
    },
    {
      id: 'pool',
      icon: Waves,
      label: {
        en: 'Swimming Pool',
        ar: 'حمام سباحة'
      }
    },
    {
      id: 'gym',
      icon: Dumbbell,
      label: {
        en: 'Gym',
        ar: 'صالة ألعاب رياضية'
      }
    },
    {
      id: 'garden',
      icon: TreePine,
      label: {
        en: 'Garden',
        ar: 'حديقة'
      }
    },
    {
      id: 'cctv',
      icon: Camera,
      label: {
        en: 'CCTV',
        ar: 'كاميرات مراقبة'
      }
    },
    {
      id: 'kitchen',
      icon: Utensils,
      label: {
        en: 'Kitchen',
        ar: 'مطبخ'
      }
    },
    {
      id: 'laundry',
      icon: WashingMachine,
      label: {
        en: 'Laundry',
        ar: 'غسيل'
      }
    }
  ];

  const handleAmenityChange = (amenityId: string, checked: boolean) => {
    if (checked) {
      onAmenitiesChange([...selectedAmenities, amenityId]);
    } else {
      onAmenitiesChange(selectedAmenities.filter(id => id !== amenityId));
    }
  };

  const currentLanguage = t('lang_code', 'ar', 'en') as 'en' | 'ar';

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b pb-2">
        {t('amenities', 'المرافق', 'Amenities')}
      </h2>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {amenities.map((amenity) => {
          const IconComponent = amenity.icon;
          const isChecked = selectedAmenities.includes(amenity.id);
          
          return (
            <div key={amenity.id} className="flex items-center space-x-2 rtl:space-x-reverse">
              <Checkbox
                id={amenity.id}
                checked={isChecked}
                onCheckedChange={(checked) => handleAmenityChange(amenity.id, checked as boolean)}
              />
              <Label
                htmlFor={amenity.id}
                className="flex items-center space-x-2 rtl:space-x-reverse cursor-pointer"
              >
                <IconComponent className="h-4 w-4 text-primary" />
                <span>{amenity.label[currentLanguage]}</span>
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PropertyAmenities;
